import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Crown } from "lucide-react";
import api, { extractApiError } from "@/lib/api";

export default function GrantMembershipDialog({ userId, userName, onGranted }) {
  const [open, setOpen] = useState(false);
  const [months, setMonths] = useState("12");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const m = parseInt(months, 10);
    if (!Number.isFinite(m) || m < 1) {
      toast.error("Enter how many months.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/admin/users/${userId}/grant-membership`, { months: m });
      toast.success(`Membership extended through ${String(data.member_until).slice(0, 10)}.`);
      setOpen(false);
      onGranted?.();
    } catch (err) {
      toast.error(extractApiError(err, "Could not grant membership."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`grant-${userId}`} className="border-amber-700/40 text-amber-800 hover:bg-amber-50">
          <Crown size={14} className="mr-1.5" /> Grant
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-stone-50 max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-stone-900">Grant membership</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm font-sans">
          <p className="text-stone-600">Extend membership for <span className="font-semibold">{userName}</span>.</p>
          <div>
            <Label htmlFor="grant-months" className="text-xs font-semibold text-stone-800">Months</Label>
            <Input
              id="grant-months"
              data-testid="grant-months"
              type="number"
              min="1"
              max="120"
              value={months}
              onChange={(e) => setMonths(e.target.value)}
              className="mt-2 bg-white"
            />
            <p className="text-xs text-stone-500 mt-1">Extends from today, or from the existing expiry if later.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="grant-submit" className="bg-emerald-800 hover:bg-emerald-700 text-stone-50">
            {submitting ? "Granting…" : "Grant"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
